import React from 'react';
import Link from 'components/commercetools-ui/atoms/link';
import Slider from 'components/commercetools-ui/atoms/slider';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import Typography from 'components/commercetools-ui/atoms/typography';
import Image from 'frontastic/lib/image';
import { Tiempos, NationalBook, NationalSemibold } from 'fonts';
import { GenericSectionContent } from '../../types';
import { useAdditionalCss } from '../../../helpers/useAdditionalCss';
import { textAlignmentClass, sanitizeHtml, SECTION_SPACING } from '../../../helpers/rendering';

// Orion ← section-with-cards (ResponsiveInline): responsive row of image cards
// matching the "Our products / Explore by Category" style on the home page.
export default function OrionRendering({ content }: { content: GenericSectionContent }) {
  const { title, description, content_alignment, cards = [], additional_css, additional_css_class_names } = content;
  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'OrionRendering');
  const align = textAlignmentClass(content_alignment);

  /**
   * The legacy two-card row was capped at 1300px (ContentSlider sets it inline when slideCount is
   * 2) and ran edge to edge above 1666px. Reproduced here so the cards keep the size they have on
   * the live home page -- without it they render ~5% wider, and the section's own heading no longer
   * lines up with the card beneath it.
   */
  const isPair = cards.length === 2;

  /**
   * How the cards lay out, and when.
   *
   * Below 643px the row scrolls sideways, showing one card and a peek of the next -- the same thing
   * the legacy Swiper carousel does, at the same size: 290px slides, 16px apart. Above it the pair
   * sits side by side. 643 is the legacy threshold
   * (`showSliderWidth = slideCount === 2 ? 642 : 1024`).
   *
   * The card width is EXPLICIT (w-[290px]) rather than `min-w-[62%]` with an auto basis. A
   * `shrink-0` item with an auto basis sizes to its content, so each card took the intrinsic width
   * of its own image -- the two came out different widths and the second was clipped by the
   * container.
   *
   * The scroll itself is wanted; its NATIVE SCROLLBAR is not, and the legacy carousel has none
   * because Swiper moves a transform rather than scrolling an overflow. Hidden on every scroller
   * here, whatever the card count; touch and drag still work.
   *
   * Written as whole literals rather than interpolated breakpoints because Tailwind only generates
   * classes it can see in the source.
   */
  /**
   * Below its threshold the section is a real CAROUSEL, as the legacy `content-slider` organism is:
   * Swiper, 290px slides 16px apart, autoplaying every 5s, no arrows and no dots. Above it, a plain
   * row. The threshold is the legacy one -- 642px for a pair, 1024px otherwise.
   *
   * `useMediaQuery()` reports 0 until it has mounted, so the FIRST paint and the server render both
   * take the row branch. That is deliberate: the CSS row below is a complete implementation on its
   * own at every width (it scrolls sideways under 643 at the same 290/16 geometry), so the pre-
   * hydration page is correct rather than merely tolerable, and Swiper takes over once it can.
   */
  const [viewportWidth] = useMediaQuery();
  const { isTouchDevice } = useTouchDevice();
  const showSlider = viewportWidth > 0 && viewportWidth <= (isPair ? 642 : 1024);

  const SCROLLER_NO_BAR = '[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';
  const rowClass = isPair
    ? `flex w-full gap-16 overflow-x-auto scroll-smooth min-[643px]:gap-24 min-[643px]:overflow-visible xl:gap-48 2xl:gap-64 3xl:gap-64 ${SCROLLER_NO_BAR}`
    : `flex w-full gap-24 overflow-x-auto scroll-smooth md:gap-24 lg:overflow-visible xl:gap-48 2xl:gap-64 3xl:gap-64 ${SCROLLER_NO_BAR}`;
  const cardClass = showSlider
    ? 'group relative block h-auto w-full overflow-hidden hover:bg-[#D9D9D9]'
    : isPair
      ? 'group relative h-auto w-[290px] shrink-0 overflow-hidden hover:bg-[#D9D9D9] min-[643px]:w-full min-[643px]:min-w-0 min-[643px]:shrink'
      : 'group relative h-auto min-w-[62%] shrink-0 overflow-hidden hover:bg-[#D9D9D9] lg:w-full lg:min-w-0 lg:shrink';

  // The legacy wrapper drops its right padding in carousel mode so the next slide peeks to the edge.
  const padX = showSlider
    ? 'pl-24 pr-0 md:px-28 lg:px-48 2xl:px-80 3xl:px-0'
    : 'px-24 md:px-28 lg:px-48 2xl:px-80 3xl:px-0';

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      {/*
        w-full is load-bearing. This container is a flex ITEM of the page's `flex flex-col`, and its
        `margin: 0 auto` (styles/components/slider.css) stops a flex item stretching -- so it shrank
        to its content, 1216px inside a 1920px page, and every card inside shrank with it. The legacy
        page put the same class on a plain block, where auto margins are harmless.
      */}
      <div className={`${SECTION_SPACING} our-product-container w-full ${padX} ${isPair ? 'max-w-[1300px]' : ''} ${[additional_css_class_names, uniqueClass].filter(Boolean).join(' ')}`}>
        {title && (
          <Typography as="h2" className={`mb-32 text-left text-32 font-medium leading-10 text-[#090909] lg:mb-40 xl:text-36 2xl:mb-48 2xl:text-48 ${Tiempos.className}`}>
            {title}
          </Typography>
        )}
        {description && (
          <div className={`mb-24 text-16 font-normal leading-8 text-[#333] 2xl:text-20 ${align}`} dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }} />
        )}
        {showSlider ? (
          <Slider
            dots={false}
            arrows={false}
            solidArrows
            allowArrowsOnTouchDevice
            allowTouchMove={isTouchDevice}
            slideWidth={290}
            spaceBetween={16}
            autoplay={{ delay: 5000, disableOnInteraction: false }}
          >
            {cards.map(renderCard)}
          </Slider>
        ) : (
          <div className={rowClass}>{cards.map(renderCard)}</div>
        )}
      </div>
    </>
  );

  function renderCard(card: (typeof cards)[number], index: number) {
    return (
            <Link key={index} link={card?.ctas?.[0]?.url ?? '#'} className={cardClass}>
              {card?.desktop_image?.url && (
                <div className="relative">
                  <div className="absolute z-10 bg-black opacity-20"></div>
                  <Image
                    priority
                    loading="eager"
                    src={encodeURI(card.desktop_image.url)}
                    alt={card.desktop_image.description ?? card?.title ?? ''}
                    sizes="100vw"
                    style={{ width: '100%', height: 'auto', objectFit: 'cover' }}
                  />
                </div>
              )}
              <div className="px-[20px] pb-[10px]">
                {card?.title && (
                  <Typography as="h4" className={`mt-12 max-w-[90%] break-words text-22 font-medium leading-[30px] text-[#090909] md:text-18 lg:text-20 xl:text-18 2xl:text-22 ${NationalSemibold.className}`}>
                    {card.title}
                  </Typography>
                )}
                {card?.description && (
                  <div className={`description mt-12 min-h-[50px] text-wrap text-16 font-normal leading-6 text-[#333] xl:text-14 2xl:text-16 ${NationalBook.className}`} dangerouslySetInnerHTML={{ __html: sanitizeHtml(card.description) }} />
                )}
                {card?.ctas && card.ctas.length > 0 && (
                  <div className="hidden md:block flex flex-col gap-1.5 group-hover:translate-x-14">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                      <path d="M18 9L25 16M25 16L18 23M25 16L7 16" stroke="#666666" className=" group-hover:stroke-[#090909]" strokeWidth="2" strokeLinecap="square" />
                    </svg>
                  </div>
                )}
              </div>
            </Link>
    );
  }
}
